import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';

// ─────────────────────────────────────────────────────────────────────────────
// formReports slice
// ─────────────────────────────────────────────────────────────────────────────
// Backs the Forms → Reports tab (FormsReportsView). Mirrors the shape of
// `formDeliveries.js` but is read-only and is keyed by formId for the
// per-form drill-down so a single store can hold the rollup plus N opened
// per-form views without thrashing each other.
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
    isLoading: false,
    error: null,
    rollup: null,           // GET /forms/reports/  → { forms: [...] , ... }
    rollupLoading: false,
    perForm: {},            // { [formId]: { summary, daily, fields, loading: {summary,daily,fields} } }
};

function ensurePerForm(state, formId) {
    if (!state.perForm[formId]) {
        state.perForm[formId] = {
            summary: null,
            daily: null,
            fields: null,
            loading: { summary: false, daily: false, fields: false },
        };
    }
    return state.perForm[formId];
}

const slice = createSlice({
    name: 'formReports',
    initialState,
    reducers: {
        startRollupLoading(state) {
            state.rollupLoading = true;
            state.isLoading = true;
        },
        setRollup(state, action) {
            state.rollup = action.payload;
            state.rollupLoading = false;
            state.isLoading = false;
        },
        startSummaryLoading(state, action) {
            const slot = ensurePerForm(state, action.payload);
            slot.loading.summary = true;
        },
        setSummary(state, action) {
            const { formId, data } = action.payload;
            const slot = ensurePerForm(state, formId);
            slot.summary = data;
            slot.loading.summary = false;
        },
        startDailyLoading(state, action) {
            const slot = ensurePerForm(state, action.payload);
            slot.loading.daily = true;
        },
        setDaily(state, action) {
            const { formId, data } = action.payload;
            const slot = ensurePerForm(state, formId);
            slot.daily = data;
            slot.loading.daily = false;
        },
        startFieldsLoading(state, action) {
            const slot = ensurePerForm(state, action.payload);
            slot.loading.fields = true;
        },
        setFields(state, action) {
            const { formId, data } = action.payload;
            const slot = ensurePerForm(state, formId);
            slot.fields = data;
            slot.loading.fields = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.rollupLoading = false;
            state.error = action.payload;
        },
        stopFormLoading(state, action) {
            const { formId, key } = action.payload;
            const slot = ensurePerForm(state, formId);
            slot.loading[key] = false;
        },
    },
});

export default slice.reducer;

// ─── Thunks ────────────────────────────────────────────────────────────────

export function getPracticeFormReportsRollup(practiceId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startRollupLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/reports/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setRollup(response.data));
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load reports' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            if (callback) callback({ success: false });
        }
    };
}

export function getFormReportSummary(practiceId, formId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startSummaryLoading(formId));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/reports/summary/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSummary({ formId, data: response.data }));
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.stopFormLoading({ formId, key: 'summary' }));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load form summary' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.stopFormLoading({ formId, key: 'summary' }));
            if (callback) callback({ success: false });
        }
    };
}

export function getFormReportDaily(practiceId, formId, since, until, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startDailyLoading(formId));
            const params = new URLSearchParams();
            if (since) params.set('since', since);
            if (until) params.set('until', until);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/reports/daily/?${params.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setDaily({ formId, data: response.data }));
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.stopFormLoading({ formId, key: 'daily' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.stopFormLoading({ formId, key: 'daily' }));
            if (callback) callback({ success: false });
        }
    };
}

export function getFormReportFields(practiceId, formId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startFieldsLoading(formId));
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/reports/fields/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setFields({ formId, data: response.data }));
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.stopFormLoading({ formId, key: 'fields' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.stopFormLoading({ formId, key: 'fields' }));
            if (callback) callback({ success: false });
        }
    };
}
